import React, { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiOffers from 'api/offers';
import IOfferProductsGet from 'api/interfaces/offers/IOfferProductsGet';
import maximize from 'assets/maximize.svg';
import minimize from 'assets/minimize.svg';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';
import { RIGHT } from 'services/enums/sides';
import { fetchInstallationCategories } from 'store/products/actions';

import OfferProductPriceSummary from './OfferProductPriceSummary';
import OfferProductsTable from './OfferProductsTable';
import RestorePositionModal from './RestorePositionModal';
import SearchPositionModal from './SearchPositionModal';

const OfferProducts = ({
  locked,
  ndaPositionsRestricted,
  offerId,
  offerSubmitted,
  productsIsOpen,
  toggleProducts,
  type,
  products,
  setProducts,
}: {
  locked: boolean;
  // The positions are NDA protected and the user is outside the NDA
  ndaPositionsRestricted: boolean;
  offerId: number | undefined;
  offerSubmitted: boolean;
  productsIsOpen: boolean;
  toggleProducts: () => void;
  type: string;
  products: null | IOfferProductsGet[];
  setProducts: (products: IOfferProductsGet[]) => void;
}): JSX.Element | null => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.products]);

  const [restoreIsOpen, setRestoreIsOpen] = useState(false);
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  // The server refused the positions (NDA)
  const [forbidden, setForbidden] = useState(false);

  // Needed to count only installations (not other expenses) in the position hours
  useEffect(() => {
    dispatch(fetchInstallationCategories());
  }, [dispatch]);

  const loadProducts = async () => {
    if (offerId === undefined) {
      return;
    }

    try {
      if (type === 'additional-offer') {
        setProducts(await apiOffers.products.getForAdditionalOffer(offerId));
        return;
      }
      setProducts(await apiOffers.products.getForOffer(offerId));
    } catch (error) {
      if (error?.response?.status === 403) {
        setForbidden(true);
        setProducts([]);
      }
    }
  };

  useEffect(() => {
    if (!productsIsOpen || products !== null || offerId === undefined) {
      return;
    }

    loadProducts();
  }, [productsIsOpen]);

  const addProduct = () => {
    history.push(`/offers/${offerId}/${type}/products/create`);
  };

  const offerIsLocked = () => locked;

  const getActionButtons = () => (
    <Row>
      <Col>
        {offerIsLocked() ? null : (
          <>
            <PrimaryButton
              float={RIGHT}
              className="button-one-line mt-3"
              onClick={addProduct}
            >
              {t(`${translations.products}:Add Product`).toLocaleUpperCase()}
            </PrimaryButton>
            <PrimaryButton
              float={RIGHT}
              className="button-one-line mt-3"
              onClick={() => setSearchIsOpen(true)}
            >
              {t(
                `${translations.offers}:positionSearch.Open`
              ).toLocaleUpperCase()}
            </PrimaryButton>
            <PrimaryButton
              float={RIGHT}
              className="button-one-line mt-3"
              onClick={() => setRestoreIsOpen(true)}
            >
              {t(
                `${translations.offers}:Restore position from earlier revision`
              ).toLocaleUpperCase()}
            </PrimaryButton>
          </>
        )}
      </Col>
    </Row>
  );

  const getContent = () => {
    if (!offerSubmitted || !productsIsOpen) {
      return null;
    }

    if (ndaPositionsRestricted || forbidden) {
      return (
        <p className="offer-nda-info">
          {t(`${translations.offers}:nda.Positions protected`)}
        </p>
      );
    }

    const productsTable = products === null ? [] : products;
    const productGroups = productsTable.reduce(
      (groups: any, product: IOfferProductsGet) => ({
        ...groups,
        [product.group]: [...(groups[product.group] || []), product],
      }),
      {}
    );

    return (
      <>
        <OfferProductsTable products={productsTable} type={type} />
        {getActionButtons()}
        <OfferProductPriceSummary products={productsTable} />
        {Object.keys(productGroups)?.map((group: any) => (
          <OfferProductPriceSummary
            key={group}
            title={t(`${translations.products}:groups:${group}`)}
            products={productGroups[group]}
          />
        ))}
        {offerId === undefined || offerIsLocked() ? null : (
          <>
            <RestorePositionModal
              isOpen={restoreIsOpen}
              offerId={offerId}
              onRestored={loadProducts}
              toggle={() => setRestoreIsOpen(!restoreIsOpen)}
              type={type}
            />
            <SearchPositionModal
              isOpen={searchIsOpen}
              offerId={offerId}
              onCopied={loadProducts}
              toggle={() => setSearchIsOpen(!searchIsOpen)}
              type={type}
            />
          </>
        )}
      </>
    );
  };
  return (
    <>
      <Row>
        <Col>
          <div
            className="header-text title-spacing"
            onClick={toggleProducts}
            role="button"
            tabIndex={0}
          >
            {productsIsOpen ? (
              <img
                src={minimize}
                alt="toggle-products"
                className="collapse-icon"
              />
            ) : (
              <img
                src={maximize}
                alt="toggle-products"
                className="collapse-icon"
              />
            )}
            {t(`${translations.products}:Products`).toUpperCase()}
          </div>
        </Col>
      </Row>
      {getContent()}
    </>
  );
};

export default OfferProducts;
